import { Col, Row, Select } from "antd";
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Joystick } from "react-joystick-component";
import "./JoystickController.css";

const VIRTUAL_GAMEPAD_ID = "Virtual Joystick";

const GamepadController = (props) => {
  const [gamepadState, setGamepadState] = useState(null);
  // Memoize URL params to prevent recreating on every render
  const { showDebug: debugFromQuery } = useMemo(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const queryJoystickShowDebug = queryParams.get("JoystickShowDebug");
    return {
      showDebug:
        queryJoystickShowDebug != null &&
        queryJoystickShowDebug.toLowerCase() === "true",
    };
  }, []);

  const MIN_AXES_VALUE = 0.05;

  // Use refs to track active event listeners for cleanup
  const activeListenersRef = useRef(new Set());

  const joyControlMappingRef = useRef(props.joyControlMapping ?? null);
  useEffect(() => {
    joyControlMappingRef.current = props.joyControlMapping ?? null;
  }, [props.joyControlMapping]);

  const showDebug = props.debug || debugFromQuery;
  const showVirtualJoystick = props.showVirtualJoystick;
  const [gamepadID, setGamepadId] = useState(props.gamepadId);

  // Memoize callback functions to prevent unnecessary re-renders
  const onGamepadStateUpdated = useMemo(
    () => props.onGamepadStateUpdated || ((evt) => {}),
    [props.onGamepadStateUpdated]
  );
  const onGamepadJoystickMoveStart = useMemo(
    () => props.onGamepadJoystickMoveStart || ((evt) => {}),
    [props.onGamepadJoystickMoveStart]
  );
  const onGamepadJoystickMoveEnd = useMemo(
    () => props.onGamepadJoystickMoveEnd || ((evt) => {}),
    [props.onGamepadJoystickMoveEnd]
  );
  const onGamepadJoystickMove = useMemo(
    () => props.onGamepadJoystickMove || ((evt) => {}),
    [props.onGamepadJoystickMove]
  );

  const [availableGamePads, setAvailableGamePads] = useState([
    VIRTUAL_GAMEPAD_ID,
  ]);

  const [isGamepadAvailable, setGamepadAvailable] = useState(null);
  const [isReady, setIsReady] = useState(false);

  const [virtualGamepadState, setVirtualGamepadState] = useState({
    axes: [0, 0, 0, 0],
    buttons: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  });

  // Memoize button constants to prevent recreating on every render
  const buttonConstants = useMemo(
    () => ({
      button_cmd_a: 0,
      button_cmd_b: 1,
      button_cmd_x: 2,
      button_cmd_y: 3,
      button_dir_forward: 12,
      button_dir_backward: 13,
      button_dir_left: 14,
      button_dir_right: 15,
      button_cmd__r1: 5,
      button_cmd__r2: 7,
      button_cmd__l1: 4,
      button_cmd__l2: 6,
    }),
    []
  );

  const isUsingVirtualGamePad = useCallback(() => {
    return gamepadID == null || gamepadID === VIRTUAL_GAMEPAD_ID;
  }, [gamepadID]);

  const triggerGamepadStateUpdated = useCallback(
    (gamepadState) => {
      if (!props.disabled && isReady) {
        const mapping = joyControlMappingRef.current;
        if (mapping) {
          const axes = new Array(mapping.axes_count).fill(0);
          for (const b of mapping.gamepad_axis_bindings) {
            axes[b.joy_index] = Math.round((gamepadState.axes[b.gamepad_index] ?? 0) * (b.scale ?? 1) * 100) / 100;
          }
          const buttons = new Array(mapping.buttons_count).fill(0);
          for (const b of mapping.gamepad_button_bindings) {
            buttons[b.joy_index] = gamepadState.buttons[b.gamepad_index] ?? 0;
          }
          onGamepadStateUpdated({ axes, buttons });
        } else {
          onGamepadStateUpdated(gamepadState);
        }
      }
    },
    [props.disabled, isReady, onGamepadStateUpdated]
  );

  useEffect(() => {
    const handleGamepadConnected = (event) => {
      console.log("Gamepad connected:", event.gamepad);
      const gamepad = event.gamepad;
      setAvailableGamePads((prevState) => {
        // Prevent duplicates
        if (prevState.includes(gamepad.id)) {
          return prevState;
        }
        return [...prevState, gamepad.id];
      });
    };

    const handleGamepadDisconnected = (event) => {
      console.log("Gamepad disconnected: ", event.gamepad);
      const gamepad = event.gamepad;
      setAvailableGamePads((prevState) => {
        return prevState.filter((item) => item !== gamepad.id);
      });
    };

    window.addEventListener("gamepadconnected", handleGamepadConnected);
    window.addEventListener("gamepaddisconnected", handleGamepadDisconnected);

    return () => {
      window.removeEventListener("gamepadconnected", handleGamepadConnected);
      window.removeEventListener(
        "gamepaddisconnected",
        handleGamepadDisconnected
      );
    };
  }, []);

  useEffect(() => {
    let animationFrameId = null;
    let isActivate = false;
    let isJoystickStarted = false;

    const updateGamepadState = () => {
      const gamepads = navigator.getGamepads();

      if (gamepads == null) {
        return;
      }

      const selectedGamepadIndex = gamepads.findIndex(
        (item) => item && item.id === gamepadID
      );

      if (selectedGamepadIndex !== -1 && gamepads[selectedGamepadIndex]) {
        setGamepadAvailable(true);
        const currentGamepadState = gamepads[selectedGamepadIndex];

        const joyValue = {
          axes: Array.from(
            currentGamepadState.axes,
            (val) => Math.round(val * 100) / 100
          ),
          buttons: Array.from(
            currentGamepadState.buttons,
            (button) => button.value
          ),
        };

        const isCurrentJoystickStarted = currentGamepadState.axes.some(
          (axis) => Math.abs(axis) >= MIN_AXES_VALUE
        );

        const isCurrentlyActive =
          isCurrentJoystickStarted ||
          currentGamepadState.buttons.some((button) => button.pressed);

        if (!isJoystickStarted && isCurrentJoystickStarted) {
          onGamepadJoystickMoveStart(joyValue);
        }

        if (isActivate || isCurrentlyActive) {
          setIsReady(true);
          setGamepadState(joyValue);
          setVirtualGamepadState(joyValue);
          triggerGamepadStateUpdated(joyValue);
          if (isJoystickStarted || isCurrentJoystickStarted) {
            onGamepadJoystickMove(joyValue);
          }
        }

        if (isJoystickStarted && !isCurrentJoystickStarted) {
          onGamepadJoystickMoveEnd(joyValue);
        }

        isActivate = isCurrentlyActive;
        isJoystickStarted = isCurrentJoystickStarted;
      } else {
        setGamepadAvailable(false);
      }
    };

    let previousCall = 0;

    const gameLoop = () => {
      const currentTime = Date.now();
      const waitingTime = currentTime - previousCall;
      if (waitingTime >= props.throttle) {
        previousCall = currentTime;
        updateGamepadState();
      }
      animationFrameId = requestAnimationFrame(gameLoop);
    };

    if (isUsingVirtualGamePad()) {
      setIsReady(true);
    } else {
      setIsReady(false);
      gameLoop();
    }

    return () => {
      if (animationFrameId != null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [
    gamepadID,
    props.throttle,
    isUsingVirtualGamePad,
    triggerGamepadStateUpdated,
    onGamepadJoystickMoveStart,
    onGamepadJoystickMove,
    onGamepadJoystickMoveEnd,
  ]);

  // Add throttling ref for joystick updates
  const lastJoystickUpdateRef = useRef(0);

  const handleJoystickMove = useCallback(
    (stick, joystickIndex) => {
      const now = Date.now();
      // Throttle joystick updates to prevent excessive re-renders
      if (now - lastJoystickUpdateRef.current < props.throttle) {
        return;
      }
      lastJoystickUpdateRef.current = now;

      setVirtualGamepadState((prev) => {
        const axesNew = [...prev.axes];
        axesNew[joystickIndex * 2] = Math.round(stick.x * 100) / 100;
        axesNew[joystickIndex * 2 + 1] = -Math.round(stick.y * 100) / 100;

        const gamepadValue = { ...prev, axes: axesNew };

        // Use requestAnimationFrame to defer heavy operations
        requestAnimationFrame(() => {
          onGamepadJoystickMove(gamepadValue);
          triggerGamepadStateUpdated(gamepadValue);
        });

        return gamepadValue;
      });
    },
    [onGamepadJoystickMove, triggerGamepadStateUpdated, props.throttle]
  );

  const handleJoystickStop = useCallback(
    (stick, joystickIndex) => {
      setVirtualGamepadState((prev) => {
        const axesNew = [...prev.axes];
        axesNew[joystickIndex * 2] = 0;
        axesNew[joystickIndex * 2 + 1] = 0;

        const gamepadValue = { ...prev, axes: axesNew };

        onGamepadJoystickMoveEnd(gamepadValue);
        triggerGamepadStateUpdated(gamepadValue);

        return gamepadValue;
      });
    },
    [onGamepadJoystickMoveEnd, triggerGamepadStateUpdated]
  );

  const handleJoystickStart = useCallback(
    (stick, joystickIndex) => {
      // Use current state via functional update to avoid stale closure
      setVirtualGamepadState((currentState) => {
        onGamepadJoystickMoveStart(currentState);
        return currentState; // No state change, just accessing current value
      });
    },
    [onGamepadJoystickMoveStart]
  );

  const handleButtonPress = useCallback(
    (evt, buttonIndex) => {
      console.log("Button ", buttonIndex, " pressed!");

      const handleRelease = (e) => {
        console.log("Button ", buttonIndex, " released!");
        setVirtualGamepadState((prev) => {
          const buttonNew = [...prev.buttons];
          buttonNew[buttonIndex] = 0;

          const gamepadValue = { ...prev, buttons: buttonNew };
          triggerGamepadStateUpdated(gamepadValue);

          return gamepadValue;
        });

        // Clean up event listeners
        evt.target.removeEventListener("mouseup", handleRelease);
        evt.target.removeEventListener("touchend", handleRelease);
        evt.target.removeEventListener("mouseleave", handleRelease);

        // Remove from active listeners tracking
        activeListenersRef.current.delete(handleRelease);
      };

      // Track active listeners for cleanup
      activeListenersRef.current.add(handleRelease);

      evt.target.addEventListener("mouseup", handleRelease);
      evt.target.addEventListener("touchend", handleRelease);
      evt.target.addEventListener("mouseleave", handleRelease);

      setVirtualGamepadState((prev) => {
        const buttonNew = [...prev.buttons];
        buttonNew[buttonIndex] = 1;

        const gamepadValue = { ...prev, buttons: buttonNew };
        triggerGamepadStateUpdated(gamepadValue);

        return gamepadValue;
      });
    },
    [triggerGamepadStateUpdated]
  );

  // Cleanup effect for active listeners
  useEffect(() => {
    return () => {
      // Clean up any remaining active listeners on unmount
      activeListenersRef.current.clear();
    };
  }, []);

  // Memoize select items to prevent recreation on every render
  const availableGamePadSelectItems = useMemo(
    () => availableGamePads.map((item) => ({ value: item, label: item })),
    [availableGamePads]
  );

  const handleChange = useCallback((value) => {
    console.log(`selected value ${value}`);
    setGamepadId(value);
  }, []);
  return (
    <div>
      {showVirtualJoystick && (
        <Row gutter={[16, 32]}>
          <Col>
            Select Joystick:{" "}
            <Select
              defaultValue={availableGamePads[0]}
              style={{ width: 200 }}
              onChange={handleChange}
              value={gamepadID}
              options={availableGamePadSelectItems}
            />
          </Col>
        </Row>
      )}
      {showVirtualJoystick && (
        <div className="gamepad">
          <button
            className="l2"
            onTouchStart={(evt) =>
              handleButtonPress(evt, buttonConstants.button_cmd__l2)
            }
            onMouseDown={(evt) =>
              handleButtonPress(evt, buttonConstants.button_cmd__l2)
            }
          >
            L2
          </button>
          <button
            className="l1"
            onTouchStart={(evt) =>
              handleButtonPress(evt, buttonConstants.button_cmd__l1)
            }
            onMouseDown={(evt) =>
              handleButtonPress(evt, buttonConstants.button_cmd__l1)
            }
          >
            L1
          </button>

          <button
            className="r2"
            onTouchStart={(evt) =>
              handleButtonPress(evt, buttonConstants.button_cmd__r2)
            }
            onMouseDown={(evt) =>
              handleButtonPress(evt, buttonConstants.button_cmd__r2)
            }
          >
            R2
          </button>
          <button
            className="r1"
            onTouchStart={(evt) =>
              handleButtonPress(evt, buttonConstants.button_cmd__r1)
            }
            onMouseDown={(evt) =>
              handleButtonPress(evt, buttonConstants.button_cmd__r1)
            }
          >
            R1
          </button>
          <div className="dpad">
            <button
              className="up"
              onTouchStart={(evt) =>
                handleButtonPress(evt, buttonConstants.button_dir_forward)
              }
              onMouseDown={(evt) =>
                handleButtonPress(evt, buttonConstants.button_dir_forward)
              }
            >
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2 L2 15 L22 15 Z" />
              </svg>
            </button>
            <button
              className="left"
              onTouchStart={(evt) =>
                handleButtonPress(evt, buttonConstants.button_dir_left)
              }
              onMouseDown={(evt) =>
                handleButtonPress(evt, buttonConstants.button_dir_left)
              }
            >
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 12 L15 2 L15 22 Z" />
              </svg>
            </button>
            <button
              className="right"
              onTouchStart={(evt) =>
                handleButtonPress(evt, buttonConstants.button_dir_right)
              }
              onMouseDown={(evt) =>
                handleButtonPress(evt, buttonConstants.button_dir_right)
              }
            >
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 12 L9 2 L9 22 Z" />
              </svg>
            </button>
            <button
              className="down"
              onTouchStart={(evt) =>
                handleButtonPress(evt, buttonConstants.button_dir_backward)
              }
              onMouseDown={(evt) =>
                handleButtonPress(evt, buttonConstants.button_dir_backward)
              }
            >
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22 L2 9 L22 9 Z" />
              </svg>
            </button>
          </div>
          <div className="abxy">
            <button
              className="y"
              onTouchStart={(evt) =>
                handleButtonPress(evt, buttonConstants.button_cmd_y)
              }
              onMouseDown={(evt) =>
                handleButtonPress(evt, buttonConstants.button_cmd_y)
              }
            >
              Y
            </button>
            <button
              className="x"
              onTouchStart={(evt) =>
                handleButtonPress(evt, buttonConstants.button_cmd_x)
              }
              onMouseDown={(evt) =>
                handleButtonPress(evt, buttonConstants.button_cmd_x)
              }
            >
              X
            </button>
            <button
              className="b"
              onTouchStart={(evt) =>
                handleButtonPress(evt, buttonConstants.button_cmd_b)
              }
              onMouseDown={(evt) =>
                handleButtonPress(evt, buttonConstants.button_cmd_b)
              }
            >
              B
            </button>
            <button
              className="a"
              onTouchStart={(evt) =>
                handleButtonPress(evt, buttonConstants.button_cmd_a)
              }
              onMouseDown={(evt) =>
                handleButtonPress(evt, buttonConstants.button_cmd_a)
              }
            >
              A
            </button>
          </div>
          <div className="joystick left-stick">
            <Joystick
              throttle={props.throttle}
              disabled={props.disabled}
              size={70}
              // baseImage="/images/icons/joystick_left_base.png"
              baseColor="Yellow"
              // stickImage="/images/icons/joystick_left_base.png"
              // stickSize="45"
              start={(stick) => handleJoystickStart(stick, 0)}
              move={(stick) => handleJoystickMove(stick, 0)}
              stop={(stick) => handleJoystickStop(stick, 0)}
              pos={{
                x: virtualGamepadState.axes[0],
                y: -virtualGamepadState.axes[1],
              }}
            ></Joystick>
          </div>
          <div className="joystick right-stick">
            <Joystick
              throttle={props.throttle}
              disabled={props.disabled}
              size={70}
              // baseImage="/images/icons/joystick_direction.png"
              baseColor="gray"
              // stickSize="40"
              start={(stick) => handleJoystickStart(stick, 1)}
              move={(stick) => handleJoystickMove(stick, 1)}
              stop={(stick) => handleJoystickStop(stick, 1)}
              pos={{
                x: virtualGamepadState.axes[2],
                y: -virtualGamepadState.axes[3],
              }}
            ></Joystick>
          </div>
        </div>
      )}
      {showDebug && (
        <div>
          {gamepadState && (
            <div>
              <h4>Controller Index: {gamepadID} </h4>
              {isGamepadAvailable && (
                <div>
                  <p>Buttons: {gamepadState.buttons.join(", ")}</p>
                  <p>Axes: {gamepadState.axes.join(", ")}</p>
                </div>
              )}
              {!isGamepadAvailable && <p>Controller not found</p>}
            </div>
          )}
          {showVirtualJoystick && (
            <div>
              <h4>Virtual controller</h4>
              <p>Buttons: {virtualGamepadState.buttons.join(", ")}</p>
              <p>Virtual Axes: {virtualGamepadState.axes.join(", ")}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

GamepadController.defaultProps = {
  debug: false,
  showVirtualJoystick: true,
  gamepadId: VIRTUAL_GAMEPAD_ID,
  disabled: false,
  throttle: 20,
};

export default GamepadController;
